import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import React, { Fragment, FunctionComponent, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgressBar } from '../../components/circular-progress-bar/CircularProgressBar';
import { Input } from '../../components/ui';
import { Origins, Roles } from '../../data/Content';
import { useAccount } from '../../hooks/useAccount';
import DefaultPageLayout from '../../layouts/defaultPageLayout';
import { submitUserOnboarding } from '../../services/segment';
import { classNames } from '../../utils/class-names';
import { BriefRoute } from '../../utils/Routes';

const QuestionnairePage: FunctionComponent = () => {
  const history = useHistory();

  const { account: currentAccount } = useAccount();
  const account = currentAccount!;

  const [role, setRole] = useState(Roles[0]);
  const [reason, setReason] = useState('');
  const [origin, setOrigin] = useState(Origins[0]);
  const [company, setCompany] = useState('');
  const [questionIndex, setQuestionIndex] = useState(0);

  const getButtonBar = (action: () => void, index: number) => {
    return (
      <>
        <div className="mt-5">
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-xs text-white bg-secondary hover:bg-secondary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
            onClick={action}
          >
            Next
          </button>
        </div>
        <div className="mt-[30px] flex w-[115px] flex-row justify-between font-['Ubuntu'] text-[12px] font-light tracking-[0.5px] text-gray-700">
          <div>Step {index} of 2</div>
          <CircularProgressBar
            removeLabel={true}
            total={2}
            current={index}
            dimmed={false}
            doneClassName="bg-primary"
            pendingClassName="bg-gray-500 opacity-70"
          />
        </div>
      </>
    );
  };

  const getHearFromUs = () => {
    //const showInputBox = origin === 'Other';

    return (
      <div className="mt-[30px] w-[500px]">
        <div className="block text-lg font-bold">How did you hear about us?</div>

        <SelectMenu activeItem={origin} items={Origins} onItemSelect={item => setOrigin(item)} />

        {/* TODO: Support adding Others */}
        {/*{showInputBox && ()}*/}
      </div>
    );
  };

  const getRole = () => {
    //const showInputBox = role === 'Other';

    return (
      <div className="mt-[30px] w-[500px]">
        <div className="block text-lg font-bold">How would you describe your role?</div>

        <SelectMenu activeItem={role} items={Roles} onItemSelect={item => setRole(item)} />

        {/* TODO: Support adding Others */}
        {/*{showInputBox && ()}*/}
      </div>
    );
  };

  const getCompany = () => {
    return (
      <div className="mt-[30px] w-[500px]">
        <div className="font-['Ubuntu'] text-[15px] font-bold tracking-[0.5px] text-black">
          What is your company name or industry?
        </div>
        <div className="mb-2 font-['Ubuntu'] text-[15px] font-light tracking-[0.5px] text-gray-400">
          We’ll help you monitor your company’s news to keep you updated?
        </div>
        <Input
          type="text"
          name="companyName"
          id="companyName"
          required
          autoComplete="off"
          placeholder="Enter your company name"
          onChange={e => {
            const companyName = e.target.value;
            setCompany(companyName);
          }}
          value={company}
        />
      </div>
    );
  };

  const getImportant = () => {
    return (
      <div className="mt-[30px] w-[500px]">
        <div className="mb-2 font-['Ubuntu'] text-[15px] font-bold tracking-[0.5px] text-black">
          Why is important for you to monitor the news?
        </div>
        <Input
          type="text"
          name="reason"
          id="reason"
          required
          autoComplete="off"
          onChange={e => {
            const reason = e.target.value;
            setReason(reason);
          }}
          value={reason}
        />
      </div>
    );
  };

  const renderQuestionScreen1 = () => {
    return (
      <div className="flex flex-col items-center justify-center">
        <h2 className="text-center">Tell us a bit about yourself </h2>
        {getRole()}
        {getHearFromUs()}
        {getButtonBar(() => {
          setQuestionIndex(1);
        }, 1)}
      </div>
    );
  };

  const handleQuestionnaireEnd = () => {
    // TODO(Jose): Handle Other responses
    // role === 'Other' ? otherRole : role
    // origin === 'Other' ? otherOrigin : origin
    submitUserOnboarding(account, {
      role: role,
      interest: company,
      referal: origin,
      reason: reason,
    });

    history.replace(BriefRoute);
  };

  const renderQuestionScreen2 = () => {
    return (
      <div className="flex flex-col items-center justify-center">
        <h2 className="text-center">Tell us a bit about your company </h2>
        {getCompany()}
        {getImportant()}
        {getButtonBar(handleQuestionnaireEnd, 2)}
      </div>
    );
  };

  return (
    <DefaultPageLayout title="Onboarding">
      <div className="flex flex-col items-center justify-center">
        <h1 className="text-center text-primary">
          <span>Welcome to Newslit!</span>
        </h1>

        <p className="max-w-[600px] text-justify font-['Ubuntu'] text-[15px] font-light tracking-[0.5px] text-gray-400">
          First, to get you the News content you are looking for, we need some information and we
          are positive that you will love your daily News briefings.
        </p>

        <hr />

        {questionIndex === 0 && renderQuestionScreen1()}

        {questionIndex === 1 && renderQuestionScreen2()}
      </div>
    </DefaultPageLayout>
  );
};

export default QuestionnairePage;

interface SelectMenuProps {
  activeItem: string;
  items: string[];
  onItemSelect: (item: string) => void;
}

function SelectMenu(props: SelectMenuProps) {
  return (
    <Listbox value={props.activeItem} onChange={props.onItemSelect}>
      {({ open }) => (
        <>
          <div className="mt-1 relative">
            <ListboxButton className="bg-white relative w-full border border-gray-300 rounded-md shadow-xs pl-3 pr-10 py-2 text-left cursor-default focus:outline-hidden focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
              <span className="block truncate">{props.activeItem}</span>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden sm:text-sm">
                {props.items.map((item, id) => (
                  <ListboxOption
                    key={id}
                    className={({ focus }) =>
                      classNames(
                        focus ? 'text-white bg-indigo-600' : 'text-gray-900',
                        'cursor-default select-none relative py-2 pl-3 pr-9'
                      )
                    }
                    value={item}
                  >
                    {({ selected, focus }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? 'font-semibold' : 'font-normal',
                            'block truncate'
                          )}
                        >
                          {item}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              focus ? 'text-white' : 'text-indigo-600',
                              'absolute inset-y-0 right-0 flex items-center pr-4'
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
}
